// Builds the grouped, filtered result list shown by the command palette.
import type { ReactNode } from 'react';
import {
  Check,
  FilePlus2,
  FileText,
  Folder,
  Hash,
  House,
  Inbox,
  Layers,
  ListTodo,
  Lock,
  LogOut,
  MessageSquare,
  Monitor,
  Moon,
  CalendarDays,
  Plus,
  Settings,
  Square,
  SquarePen,
  Sun,
  Timer,
  Users,
} from '@/components/ui/icons';
import type { Channel, Doc, Location, Task, User } from '@shared/types';
import { signOut, type Tables } from '@/lib/store';
import { createDoc, openDm, runningTimer, stopTimer } from '@/lib/actions';
import { effectiveStatuses, isTaskClosed, locationName, statusOf } from '@/lib/hierarchy';
import { locationPath } from '@/lib/nav';
import { applyTheme, pushRecent, useUi, type RecentItem } from '@/lib/ui';
import { relativeTime } from '@/lib/format';
import { Avatar } from '@/components/ui/Avatar';
import { StatusIcon, statusProgress } from '@/components/fields/Status';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { channelTitle } from '@/shell/ChannelSections';
import { openDialog } from '@/shell/dialogs';
import { Highlight, matchesAll, matchScore, tokenize } from './search';
import { isGuest, roleOf } from '@shared/roles';

export type FilterKey = 'all' | 'tasks' | 'docs' | 'chat' | 'people' | 'lists';

export const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'docs', label: 'Docs' },
  { key: 'chat', label: 'Chat' },
  { key: 'people', label: 'People' },
  { key: 'lists', label: 'Lists' },
];

export type GroupKey = 'recent' | 'tasks' | 'locations' | 'docs' | 'channels' | 'people' | 'commands';

export interface PaletteItem {
  key: string;
  kind: 'task' | 'location' | 'doc' | 'channel' | 'person' | 'command' | 'more';
  icon: ReactNode;
  /** plain title (highlighted at render time unless `plain`) */
  title: string;
  plain?: boolean;
  meta?: ReactNode;
  right?: ReactNode;
  run: () => void;
  /** don't close the palette after running (e.g. "Show all") */
  keepOpen?: boolean;
}

export interface PaletteGroup {
  key: GroupKey;
  label: string;
  items: PaletteItem[];
  total: number;
  /** results beyond the cap that aren't reachable via "Show all" */
  hidden: number;
}

export interface PaletteEnv {
  db: Tables;
  ws: string;
  me: string;
  theme: 'dark' | 'light' | 'system';
  recents: RecentItem[];
  words: string[];
  go: (path: string) => void;
  openTask: (id: string) => void;
  setFilter: (f: FilterKey) => void;
}

const ALL_CAP = 6;
const RECENT_CAP = 8;
const FILTER_CAP = 80;

// ---------------------------------------------------------------------------
// single-entity items

function taskWorkspace(db: Tables, t: Task) {
  return t.workspaceId || db.spaces[db.lists[t.listId]?.spaceId]?.workspaceId;
}

function TaskStatus({ db, task }: { db: Tables; task: Task }) {
  const status = statusOf(db, task);
  return <StatusIcon type={status.type} color={status.color} size={15} progress={statusProgress(effectiveStatuses(db, task.listId), status)} />;
}

export function taskItem(env: PaletteEnv, task: Task, opts: { idMatch?: boolean } = {}): PaletteItem {
  const list = env.db.lists[task.listId];
  const parent = task.parentId ? env.db.tasks[task.parentId] : undefined;
  return {
    key: `task:${task.id}`,
    kind: 'task',
    icon: <TaskStatus db={env.db} task={task} />,
    title: task.name,
    meta: (
      <>
        {parent ? `${parent.name} · ` : ''}
        {list?.name ?? ''}
      </>
    ),
    right: <span className={opts.idMatch ? 'cmdk-id match' : 'cmdk-id'}>#{task.id}</span>,
    run: () => env.openTask(task.id),
  };
}

function locationIcon(db: Tables, loc: Location): ReactNode {
  if (loc.type === 'space') return <SpaceAvatar spaceId={loc.id} size={16} />;
  if (loc.type === 'folder') {
    const color = db.folders[loc.id]?.color;
    return <Folder size={15} style={color ? { color } : undefined} className={color ? undefined : 'faint'} />;
  }
  if (loc.type === 'list') {
    const color = db.lists[loc.id]?.color;
    return <ListTodo size={15} style={color ? { color } : undefined} className={color ? undefined : 'faint'} />;
  }
  return <Layers size={15} className="faint" />;
}

function locationMeta(db: Tables, loc: Location): string {
  if (loc.type === 'list') {
    const l = db.lists[loc.id];
    if (!l) return '';
    const folder = l.folderId ? db.folders[l.folderId]?.name : null;
    return [db.spaces[l.spaceId]?.name, folder].filter(Boolean).join(' / ');
  }
  if (loc.type === 'folder') return db.spaces[db.folders[loc.id]?.spaceId]?.name ?? '';
  if (loc.type === 'space') return 'Space';
  return 'All tasks';
}

export function locationItem(env: PaletteEnv, loc: Location): PaletteItem {
  return {
    key: `loc:${loc.type}:${loc.id}`,
    kind: 'location',
    icon: locationIcon(env.db, loc),
    title: loc.type === 'workspace' ? 'Everything' : locationName(env.db, loc),
    meta: locationMeta(env.db, loc),
    run: () => env.go(locationPath(env.ws, loc)),
  };
}

function docIcon(doc: Doc) {
  if (doc.icon && !/^[A-Z][A-Za-z0-9]+$/.test(doc.icon)) return <span className="cmdk-emoji">{doc.icon}</span>;
  return <FileText size={15} className="cmdk-doc-icon" />;
}

export function docItem(env: PaletteEnv, doc: Doc): PaletteItem {
  return {
    key: `doc:${doc.id}`,
    kind: 'doc',
    icon: docIcon(doc),
    title: doc.title || 'Untitled',
    meta: doc.location ? `in ${locationName(env.db, doc.location)}` : 'Workspace',
    right: <span className="cmdk-right-faint">{relativeTime(doc.updatedAt ?? doc.createdAt)}</span>,
    run: () => {
      pushRecent({ kind: 'doc', id: doc.id });
      env.go(`/${env.ws}/docs/${doc.id}`);
    },
  };
}

export function channelItem(env: PaletteEnv, ch: Channel): PaletteItem {
  const others = ch.memberIds.filter((u) => u !== env.me);
  const icon =
    ch.kind === 'dm' ? (
      <Avatar userId={others[0] ?? env.me} size={18} showPresence />
    ) : ch.private ? (
      <Lock size={15} className="faint" />
    ) : (
      <Hash size={15} className="faint" />
    );
  return {
    key: `ch:${ch.id}`,
    kind: 'channel',
    icon,
    title: channelTitle(env.db, ch, env.me),
    meta: ch.kind === 'dm' ? 'Direct message' : `${ch.memberIds.length} member${ch.memberIds.length === 1 ? '' : 's'}`,
    run: () => {
      pushRecent({ kind: 'channel', id: ch.id });
      env.go(`/${env.ws}/chat/${ch.id}`);
    },
  };
}

function emailMeta(email: string, words: string[]): ReactNode {
  if (words.some((w) => w.includes('@') || w.includes('.'))) return <Highlight text={email} words={words} />;
  const at = email.indexOf('@');
  if (at < 0) return <Highlight text={email} words={words} />;
  return (
    <>
      <Highlight text={email.slice(0, at)} words={words} />
      {email.slice(at)}
    </>
  );
}

export function personItem(env: PaletteEnv, user: User): PaletteItem {
  return {
    key: `user:${user.id}`,
    kind: 'person',
    icon: <Avatar userId={user.id} size={20} showPresence />,
    title: user.id === env.me ? `${user.name} (you)` : user.name,
    meta: emailMeta(user.email, env.words),
    right: user.title ? <span className="cmdk-right-faint">{user.title}</span> : undefined,
    run: () => {
      const ch = openDm(env.ws, user.id);
      pushRecent({ kind: 'channel', id: ch.id });
      env.go(`/${env.ws}/chat/${ch.id}`);
    },
  };
}

// ---------------------------------------------------------------------------
// category searches (uncapped; sorted best-first)

function searchTasks(env: PaletteEnv, rawQuery: string): PaletteItem[] {
  const { db, ws, words } = env;
  const idQuery = rawQuery.trim().replace(/^#/, '').toLowerCase();
  const hits: { task: Task; id: boolean; closed: boolean; score: number }[] = [];
  for (const t of Object.values(db.tasks)) {
    if (t.archived || !db.lists[t.listId] || taskWorkspace(db, t) !== ws) continue;
    const id = idQuery.length > 0 && t.id.toLowerCase() === idQuery;
    if (!id && !matchesAll(words, t.name)) continue;
    hits.push({ task: t, id, closed: isTaskClosed(db, t), score: matchScore(t.name, words) });
  }
  hits.sort(
    (a, b) =>
      Number(b.id) - Number(a.id) ||
      Number(a.closed) - Number(b.closed) ||
      a.score - b.score ||
      (b.task.updatedAt ?? b.task.createdAt) - (a.task.updatedAt ?? a.task.createdAt),
  );
  return hits.map((h) => taskItem(env, h.task, { idMatch: h.id }));
}

function searchLocations(env: PaletteEnv): PaletteItem[] {
  const { db, ws, words } = env;
  const spaces = Object.values(db.spaces)
    .filter((s) => s.workspaceId === ws && !s.archived)
    .sort((a, b) => a.orderIndex - b.orderIndex);
  // tree order: space, its folders (+ their lists), then folderless lists
  const ordered: Location[] = [];
  for (const s of spaces) {
    ordered.push({ type: 'space', id: s.id });
    const folders = Object.values(db.folders)
      .filter((f) => f.spaceId === s.id && !f.archived)
      .sort((a, b) => a.orderIndex - b.orderIndex);
    for (const f of folders) {
      ordered.push({ type: 'folder', id: f.id });
      for (const l of Object.values(db.lists)
        .filter((l) => l.folderId === f.id && !l.archived)
        .sort((a, b) => a.orderIndex - b.orderIndex))
        ordered.push({ type: 'list', id: l.id });
    }
    for (const l of Object.values(db.lists)
      .filter((l) => l.spaceId === s.id && !l.folderId && !l.archived)
      .sort((a, b) => a.orderIndex - b.orderIndex))
      ordered.push({ type: 'list', id: l.id });
  }
  if (words.length === 0) return ordered.map((loc) => locationItem(env, loc));
  const rank = { list: 0, folder: 1, space: 2, workspace: 3 } as const;
  return ordered
    .map((loc, i) => ({ loc, i, name: locationName(db, loc) }))
    .filter((x) => matchesAll(words, x.name))
    .sort((a, b) => matchScore(a.name, words) - matchScore(b.name, words) || rank[a.loc.type] - rank[b.loc.type] || a.i - b.i)
    .map((x) => locationItem(env, x.loc));
}

function searchDocs(env: PaletteEnv): PaletteItem[] {
  const { db, ws, words } = env;
  return Object.values(db.docs)
    .filter((d) => d.workspaceId === ws && !d.archived && matchesAll(words, d.title || 'Untitled'))
    .sort((a, b) => matchScore(a.title, words) - matchScore(b.title, words) || (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt))
    .map((d) => docItem(env, d));
}

function visibleChannels(db: Tables, ws: string, me: string): Channel[] {
  return Object.values(db.channels).filter(
    (c) => c.workspaceId === ws && (c.kind === 'dm' ? c.memberIds.includes(me) : !c.private || c.memberIds.includes(me)),
  );
}

function searchChannels(env: PaletteEnv): PaletteItem[] {
  const { db, ws, me, words } = env;
  return visibleChannels(db, ws, me)
    .map((c) => ({ c, title: channelTitle(db, c, me) }))
    .filter((x) => matchesAll(words, x.title))
    .sort(
      (a, b) =>
        matchScore(a.title, words) - matchScore(b.title, words) ||
        Number(a.c.kind === 'dm') - Number(b.c.kind === 'dm') ||
        a.title.localeCompare(b.title),
    )
    .map((x) => channelItem(env, x.c));
}

function searchPeople(env: PaletteEnv): PaletteItem[] {
  const { db, ws, words } = env;
  const ids = db.workspaces[ws]?.memberIds ?? [];
  // match the email's local part only, unless the query looks like an address/domain
  const fullEmail = words.some((w) => w.includes('@') || w.includes('.'));
  return ids
    .map((id) => db.users[id])
    .filter((u): u is User => !!u && matchesAll(words, u.name, fullEmail ? u.email : u.email.split('@')[0]))
    .sort((a, b) => matchScore(a.name, words) - matchScore(b.name, words) || a.name.localeCompare(b.name))
    .map((u) => personItem(env, u));
}

// ---------------------------------------------------------------------------
// commands

interface CommandDef {
  id: string;
  title: string;
  keywords?: string;
  icon: ReactNode;
  right?: ReactNode;
  run: () => void;
}

function setTheme(theme: 'dark' | 'light' | 'system') {
  useUi.setState({ theme });
  applyTheme(theme);
}

function commandDefs(env: PaletteEnv): CommandDef[] {
  const { ws, go, theme } = env;
  const kbd = (k: string) => <span className="kbd">{k}</span>;
  const check = (on: boolean) => (on ? <Check size={14} className="cmdk-check" /> : undefined);
  const timer = runningTimer(env.db, env.me);
  const defs: CommandDef[] = [
    { id: 'create-task', title: 'Create task', keywords: 'new add task', icon: <SquarePen size={15} />, right: kbd('T'), run: () => useUi.setState({ createTaskOpen: true }) },
    {
      id: 'new-doc',
      title: 'New Doc',
      keywords: 'create document page write',
      icon: <FilePlus2 size={15} />,
      run: () => {
        const { doc } = createDoc(ws, null);
        pushRecent({ kind: 'doc', id: doc.id });
        go(`/${ws}/docs/${doc.id}`);
      },
    },
    { id: 'new-space', title: 'New Space', keywords: 'create space team department', icon: <Plus size={15} />, run: () => openDialog({ kind: 'space' }) },
    { id: 'go-inbox', title: 'Go to Inbox', keywords: 'notifications', icon: <Inbox size={15} />, run: () => go(`/${ws}/inbox`) },
    { id: 'go-home', title: 'Go to My Tasks', keywords: 'home my work', icon: <House size={15} />, run: () => go(`/${ws}/home`) },
    { id: 'go-everything', title: 'Go to Everything', keywords: 'all tasks', icon: <Layers size={15} />, run: () => go(`/${ws}/everything`) },
    { id: 'go-planner', title: 'Go to Planner', keywords: 'calendar schedule', icon: <CalendarDays size={15} />, run: () => go(`/${ws}/planner`) },
    { id: 'go-chat', title: 'Go to Chat', keywords: 'messages channels', icon: <MessageSquare size={15} />, run: () => go(`/${ws}/chat`) },
    { id: 'go-docs', title: 'Go to Docs', keywords: 'documents wiki', icon: <FileText size={15} />, run: () => go(`/${ws}/docs`) },
    { id: 'go-teams', title: 'Go to Teams', keywords: 'people members', icon: <Users size={15} />, run: () => go(`/${ws}/teams`) },
    { id: 'go-timesheets', title: 'Go to Timesheets', keywords: 'time tracking hours', icon: <Timer size={15} />, run: () => go(`/${ws}/timesheets`) },
    { id: 'go-settings', title: 'Open Settings', keywords: 'preferences workspace profile task types', icon: <Settings size={15} />, run: () => go(`/${ws}/settings`) },
  ];
  // guests can't create docs or spaces
  if (isGuest(roleOf(env.db.workspaces[ws], env.me))) defs.splice(0, defs.length, ...defs.filter((d) => d.id !== 'new-doc' && d.id !== 'new-space'));
  if (timer) {
    const t = env.db.tasks[timer.taskId];
    defs.push({ id: 'stop-timer', title: 'Stop timer', keywords: `time tracking ${t?.name ?? ''}`, icon: <Square size={14} />, right: t ? <span className="cmdk-right-faint truncate">{t.name}</span> : undefined, run: () => stopTimer() });
  }
  defs.push(
    { id: 'theme-dark', title: 'Switch to dark theme', keywords: 'theme appearance mode night', icon: <Moon size={15} />, right: check(theme === 'dark'), run: () => setTheme('dark') },
    { id: 'theme-light', title: 'Switch to light theme', keywords: 'theme appearance mode day', icon: <Sun size={15} />, right: check(theme === 'light'), run: () => setTheme('light') },
    { id: 'theme-system', title: 'Use system theme', keywords: 'theme appearance mode auto', icon: <Monitor size={15} />, right: check(theme === 'system'), run: () => setTheme('system') },
    {
      id: 'logout',
      title: 'Log out',
      keywords: 'sign out switch user',
      icon: <LogOut size={15} />,
      run: () => {
        void signOut();
        go('/login');
      },
    },
  );
  return defs;
}

function searchCommands(env: PaletteEnv): PaletteItem[] {
  const { words } = env;
  return commandDefs(env)
    .filter((c) => matchesAll(words, c.title, c.keywords))
    .sort((a, b) => matchScore(a.title, words) - matchScore(b.title, words))
    .map((c) => ({ key: `cmd:${c.id}`, kind: 'command', icon: c.icon, title: c.title, right: c.right, run: c.run }));
}

// ---------------------------------------------------------------------------
// recents

function recentItems(env: PaletteEnv): PaletteItem[] {
  const { db, ws, me } = env;
  const out: PaletteItem[] = [];
  for (const r of env.recents) {
    if (out.length >= RECENT_CAP) break;
    if (r.kind === 'task') {
      const t = db.tasks[r.id];
      if (t && !t.archived && taskWorkspace(db, t) === ws) out.push(taskItem(env, t));
    } else if (r.kind === 'doc') {
      const d = db.docs[r.id];
      if (d && !d.archived && d.workspaceId === ws) out.push(docItem(env, d));
    } else if (r.kind === 'channel') {
      const c = db.channels[r.id];
      if (c && c.workspaceId === ws && visibleChannels(db, ws, me).includes(c)) out.push(channelItem(env, c));
    } else if (r.kind === 'location') {
      const loc = r.location ?? ({ type: 'list', id: r.id } as Location);
      const exists =
        loc.type === 'workspace'
          ? loc.id === ws
          : loc.type === 'space'
            ? db.spaces[loc.id]?.workspaceId === ws && !db.spaces[loc.id]?.archived
            : loc.type === 'folder'
              ? db.spaces[db.folders[loc.id]?.spaceId]?.workspaceId === ws && !db.folders[loc.id]?.archived
              : db.spaces[db.lists[loc.id]?.spaceId]?.workspaceId === ws && !db.lists[loc.id]?.archived;
      if (exists) out.push(locationItem(env, loc));
    }
  }
  return out;
}

// ---------------------------------------------------------------------------

const GROUP_FOR_FILTER: Record<Exclude<FilterKey, 'all'>, { key: GroupKey; label: string; noun: string }> = {
  tasks: { key: 'tasks', label: 'Tasks', noun: 'tasks' },
  lists: { key: 'locations', label: 'Lists, Folders & Spaces', noun: 'locations' },
  docs: { key: 'docs', label: 'Docs', noun: 'docs' },
  chat: { key: 'channels', label: 'Channels & DMs', noun: 'conversations' },
  people: { key: 'people', label: 'People', noun: 'people' },
};

export function buildGroups(base: Omit<PaletteEnv, 'words'>, query: string, filter: FilterKey): PaletteGroup[] {
  const words = tokenize(query);
  const env: PaletteEnv = { ...base, words };
  const groups: PaletteGroup[] = [];

  if (words.length === 0 && filter === 'all') {
    const recent = recentItems(env);
    if (recent.length) groups.push({ key: 'recent', label: 'Recent', items: recent, total: recent.length, hidden: 0 });
    const cmds = searchCommands(env);
    groups.push({ key: 'commands', label: 'Commands', items: cmds, total: cmds.length, hidden: 0 });
    return groups;
  }

  const search: Record<Exclude<FilterKey, 'all'>, () => PaletteItem[]> = {
    tasks: () => searchTasks(env, query),
    lists: () => searchLocations(env),
    docs: () => searchDocs(env),
    chat: () => searchChannels(env),
    people: () => searchPeople(env),
  };

  for (const f of ['tasks', 'lists', 'docs', 'chat', 'people'] as const) {
    if (filter !== 'all' && filter !== f) continue;
    const all = search[f]();
    if (all.length === 0) continue;
    const meta = GROUP_FOR_FILTER[f];
    const cap = filter === 'all' ? ALL_CAP : FILTER_CAP;
    const items = all.slice(0, cap);
    if (filter === 'all' && all.length > cap) {
      items.push({
        key: `more:${f}`,
        kind: 'more',
        icon: null,
        title: `Show all ${all.length} ${meta.noun}`,
        plain: true,
        keepOpen: true,
        run: () => env.setFilter(f),
      });
    }
    groups.push({ key: meta.key, label: meta.label, items, total: all.length, hidden: filter === 'all' ? 0 : Math.max(0, all.length - cap) });
  }

  if (filter === 'all') {
    const cmds = searchCommands(env).slice(0, ALL_CAP);
    if (cmds.length) groups.push({ key: 'commands', label: 'Commands', items: cmds, total: cmds.length, hidden: 0 });
  }
  return groups;
}
